// Theme and CSS variable helpers – ported from App.js lines 504-622 and 103-160

import type { AppSettings, ThemeColor } from './cardUtils.js';
import vcardTheme from '../theme/vcard';
import minimalTheme from '../theme/minimal';
import mintTheme from '../theme/mint';

type ThemeFile = {
  colors?: Record<string, { light?: string; dark?: string }>;
  textures?: { main?: { light?: string; dark?: string; size?: string; blendLight?: string; blendDark?: string; opacityLight?: number; opacityDark?: number } };
};

const THEME_FILES: Record<string, ThemeFile> = {
  vcard: vcardTheme as unknown as ThemeFile,
  minimal: minimalTheme as unknown as ThemeFile,
  mint: mintTheme as unknown as ThemeFile,
};

const APP_THEME_OVERRIDE_TOKENS = [
  'main', 'card', 'surface', 'action', 'actionHover',
  'textPrimary', 'textSecondary', 'textMuted', 'textMutedSubtle',
  'border', 'borderSubtle', 'success', 'successHover', 'successBg',
  'successText', 'successBorder', 'error', 'errorHover', 'errorBg',
  'errorText', 'errorBorder', 'info', 'infoHover', 'infoBg',
  'infoText', 'infoBorder', 'link', 'linkBg', 'linkBorder', 'linkHover',
  'inputBg', 'inputBorder', 'inputText', 'focusRing', 'overlay',
  'confirm', 'confirmHover', 'confirmText', 'confirmBg', 'confirmBorder'
];

// ---------------------------------------------------------------------------
// Normalize theme variant name
// ---------------------------------------------------------------------------
export const normalizeThemeVariantClient = (variant: unknown): string => {
  const normalized = typeof variant === 'string' ? variant.trim().toLowerCase() : '';
  if (normalized === 'nagarro') return 'mint';
  if (normalized === 'vcard' || normalized === 'minimal' || normalized === 'mint' || normalized === 'custom') {
    return normalized;
  }
  return 'vcard';
};

// ---------------------------------------------------------------------------
// Apply theme CSS variables to :root
// ---------------------------------------------------------------------------
const toKebabCase = (str: string): string =>
  str.replace(/([A-Z])/g, (match) => '-' + match).toLowerCase();

export const applyThemeCssVars = (
  variant: unknown,
  overrides: Record<string, { light?: string; dark?: string }> | null = null
): void => {
  const root = document.documentElement;
  const normalizedVariant = normalizeThemeVariantClient(variant);
  const theme = THEME_FILES[normalizedVariant] || vcardTheme;
  const colors = (theme.colors || {}) as Record<string, { light?: string; dark?: string }>;

  // Set all color CSS variables from theme
  Object.keys(colors).forEach((colorKey) => {
    const colorValue = colors[colorKey];
    if (colorValue && typeof colorValue === 'object' && colorValue.light !== undefined) {
      const cssVarName = `--color-${toKebabCase(colorKey)}`;
      root.style.setProperty(`${cssVarName}-light`, colorValue.light ?? '');
      root.style.setProperty(`${cssVarName}-dark`, colorValue.dark ?? '');
    }
  });

  // Set texture variables
  const textures = theme.textures?.main || {};
  root.style.setProperty('--texture-main-light', textures.light ? `url(${textures.light})` : 'none');
  root.style.setProperty('--texture-main-dark', textures.dark ? `url(${textures.dark})` : 'none');
  root.style.setProperty('--texture-main-size', textures.size || '540px 540px');
  root.style.setProperty('--texture-main-blend-light', textures.blendLight || 'multiply');
  root.style.setProperty('--texture-main-blend-dark', textures.blendDark || 'overlay');
  root.style.setProperty('--texture-main-opacity-light', String(textures.opacityLight ?? 0.08));
  root.style.setProperty('--texture-main-opacity-dark', String(textures.opacityDark ?? 0.1));

  // Apply per-organisation app color overrides after base theme vars
  if (overrides && typeof overrides === 'object') {
    const allowedKeys = new Set(APP_THEME_OVERRIDE_TOKENS);
    Object.keys(overrides).forEach((rawKey) => {
      const token = String(rawKey || '').replace(/[^a-zA-Z0-9_-]/g, '');
      if (!allowedKeys.has(token)) return;
      if (!token) return;
      const value = overrides[rawKey];
      if (!value || typeof value !== 'object') return;
      if (typeof value.light === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value.light.trim())) {
        root.style.setProperty(`--color-${toKebabCase(token)}-light`, value.light.trim());
      }
      if (typeof value.dark === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value.dark.trim())) {
        root.style.setProperty(`--color-${toKebabCase(token)}-dark`, value.dark.trim());
      }
    });
  }
};

// ---------------------------------------------------------------------------
// Color helpers from settings.theme_colors
// ---------------------------------------------------------------------------
export const getThemeGradient = (colorName: string | undefined, settings: AppSettings | undefined): string => {
  if (!colorName) return 'linear-gradient(135deg, #4f46e5, #7c3aed)';
  if (colorName.startsWith('#')) {
    const h = colorName.replace('#', '');
    const toH = (n: number) => n.toString(16).padStart(2, '0');
    const darker = `#${toH(Math.max(0, Math.floor(parseInt(h.slice(0,2),16)*0.80)))}${toH(Math.max(0, Math.floor(parseInt(h.slice(2,4),16)*0.80)))}${toH(Math.max(0, Math.floor(parseInt(h.slice(4,6),16)*0.80)))}`;
    return `linear-gradient(135deg, ${colorName}, ${darker})`;
  }
  if (settings?.theme_colors?.length) {
    const color = settings.theme_colors.find((c: ThemeColor) => c.name === colorName);
    if (color?.gradientStyle) return color.gradientStyle;
  }
  const hex600 = getTailwindColorHex(colorName, 600);
  const hex800 = getTailwindColorHex(colorName, 800);
  return `linear-gradient(135deg, ${hex600}, ${hex800})`;
};

export const getButtonColor = (colorName: string | undefined, settings: AppSettings | undefined): string => {
  if (!colorName) return '#4f46e5';
  if (colorName.startsWith('#')) return colorName;
  if (settings?.theme_colors?.length) {
    const color = settings.theme_colors.find((c: ThemeColor) => c.name === colorName);
    if (color?.buttonStyle) return color.buttonStyle;
  }
  return getTailwindColorHex(colorName, 600);
};

export const getLinkColor = (colorName: string | undefined, settings: AppSettings | undefined): string => {
  if (!colorName) return '#4f46e5';
  if (colorName.startsWith('#')) return colorName;
  if (settings?.theme_colors?.length) {
    const color = settings.theme_colors.find((c: ThemeColor) => c.name === colorName);
    if (color?.linkStyle) return color.linkStyle;
  }
  return getTailwindColorHex(colorName, 600);
};

export const getTextColor = (colorName: string | undefined, settings: AppSettings | undefined): string => {
  if (!colorName) return '#4f46e5';
  if (colorName.startsWith('#')) return colorName;
  if (settings?.theme_colors?.length) {
    const color = settings.theme_colors.find((c: ThemeColor) => c.name === colorName);
    if (color?.textStyle) return color.textStyle;
  }
  return getTailwindColorHex(colorName, 600);
};

// ---------------------------------------------------------------------------
// Tailwind color utilities
// ---------------------------------------------------------------------------
export const TAILWIND_COLORS = [
  'indigo', 'blue', 'rose', 'emerald', 'slate', 'purple', 'cyan', 'teal',
  'orange', 'pink', 'violet', 'fuchsia', 'amber', 'lime', 'green', 'yellow', 'red'
];

const COMPLEMENTARY_MAP: Record<string, string> = {
  indigo: 'purple', blue: 'cyan', rose: 'orange', emerald: 'teal',
  slate: 'slate', purple: 'indigo', cyan: 'blue', teal: 'emerald',
  orange: 'rose', pink: 'fuchsia', violet: 'purple', fuchsia: 'pink',
  amber: 'orange', lime: 'green', green: 'emerald', yellow: 'amber',
  red: 'rose'
};

export const getComplementaryColor = (baseColor: string): string =>
  COMPLEMENTARY_MAP[baseColor] || 'purple';

export const getTailwindShades = (): number[] => [400, 500, 600, 700, 800];

export const TAILWIND_TO_HEX: Record<string, Record<number, string>> = {
  indigo:  { 400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3' },
  blue:    { 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af' },
  rose:    { 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239' },
  emerald: { 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46' },
  slate:   { 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b' },
  purple:  { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  cyan:    { 400: '#22d3ee', 500: '#06b6d4', 600: '#0891b2', 700: '#0e7490', 800: '#155e75' },
  teal:    { 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59' },
  orange:  { 400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412' },
  pink:    { 400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d', 800: '#9f1239' },
  violet:  { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  fuchsia: { 400: '#f0abfc', 500: '#d946ef', 600: '#c026d3', 700: '#a21caf', 800: '#86198f' },
  amber:   { 400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e' },
  lime:    { 400: '#a3e635', 500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f', 800: '#365314' },
  green:   { 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534' },
  yellow:  { 400: '#facc15', 500: '#eab308', 600: '#ca8a04', 700: '#a16207', 800: '#854d0e' },
  red:     { 400: '#f87171', 500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b' }
};

export const getTailwindColorHex = (colorName: string, shade = 600): string =>
  TAILWIND_TO_HEX[colorName]?.[shade] || '#4f46e5';

export const darkenHex = (hex: string, percent: number): string => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const factor = 1 - percent / 100;
  const newR = Math.max(0, Math.floor(r * factor));
  const newG = Math.max(0, Math.floor(g * factor));
  const newB = Math.max(0, Math.floor(b * factor));
  const toHex = (n: number): string => {
    const h = n.toString(16);
    return h.length === 1 ? '0' + h : h;
  };
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

export const extractBaseColorFromGradient = (gradient: string | null | undefined): { baseColor: string; shade: number } | null => {
  if (!gradient) return null;
  const match = gradient.match(/from-(\w+)-(\d+)/);
  if (match) {
    const baseColor = match[1] ?? '';
    const shadeStr = match[2] ?? '0';
    return { baseColor, shade: parseInt(shadeStr) };
  }
  return null;
};
